import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Search, LayoutGrid, List as ListIcon, Plus, Download, Trash2, Key, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { EmptyState } from "@/components/app/Skeleton";

const ROLES = ["super_admin","hr_admin","manager","team_lead","department_head","finance","it_support","admin_ops","employee"];

export default function People() {
  const { user } = useAuth();
  const canManage = ["super_admin", "hr_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";
  const [resetTarget, setResetTarget] = useState(null); // {id, full_name, user_id?}
  const [resetPw, setResetPw] = useState("");
  const [resetForce, setResetForce] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const submitAdminReset = async () => {
    if (!resetTarget) return;
    if ((resetPw || "").length < 8) return toast.error("Password must be at least 8 characters");
    setResetBusy(true);
    try {
      // Look up user_id from employee_id if not passed
      let uid = resetTarget.user_id;
      if (!uid) {
        const r = await api.get(`/employees/${resetTarget.id}`);
        uid = r.data?.user_id;
      }
      if (!uid) throw new Error("No linked user account");
      await api.post(`/admin/users/${uid}/set-password`, { new_password: resetPw, force_change_on_next_login: resetForce });
      toast.success(`Password reset for ${resetTarget.full_name}`);
      setResetTarget(null); setResetPw(""); setResetForce(false);
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Failed"); }
    finally { setResetBusy(false); }
  };
  const [emps, setEmps] = useState([]);
  const [depts, setDepts] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [view, setView] = useState("list");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", role: "employee", designation: "", department_id: "", manager_id: "", location: "" });
  const nav = useNavigate();

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (dept !== "all") params.department_id = dept;
    const [e, d] = await Promise.all([api.get("/employees", { params }), api.get("/departments")]);
    setEmps(e.data); setDepts(d.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dept]);
  const loc = useLocation();
  useEffect(() => { if (loc.state?.openAdd && canManage) setOpen(true); }, [loc.state, canManage]);

  const create = async () => {
    try {
      const payload = { ...form, manager_id: form.manager_id || null, department_id: form.department_id || null };
      await api.post("/employees", payload);
      toast.success("Employee added · default password: password123");
      setOpen(false);
      setForm({ full_name: "", email: "", role: "employee", designation: "", department_id: "", manager_id: "", location: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Mark this employee as inactive?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Employee archived"); load(); }
    catch { toast.error("Failed"); }
  };

  const exportCsv = () => {
    const rows = [["Name", "Email", "Role", "Designation", "Department", "Location", "Status"]];
    emps.forEach((e) => {
      rows.push([e.full_name, e.email, e.role, e.designation, depts.find(d => d.id === e.department_id)?.name || "", e.location, e.employment_status]);
    });
    const csv = rows.map((r) => r.map((x) => `"${(x || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `employees-${Date.now()}.csv`; a.click();
    toast.success("Exported CSV");
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="people-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">People</div>
          <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Employee Directory</h1>
          <p className="text-sm text-slate-600 mt-1">{emps.length} members</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv"><Download className="w-4 h-4 mr-1"/>Export CSV</Button>
          {canManage && (
            <Button variant="outline" size="sm" asChild data-testid="bulk-import-btn">
              <Link to="/people/import"><Upload className="w-4 h-4 mr-1"/>Bulk Import</Link>
            </Button>
          )}
          {canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-1"/>Add Employee</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Add New Employee</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Full Name</Label><Input value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="mt-1" data-testid="new-emp-name"/></div>
                    <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1" data-testid="new-emp-email"/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({...form, designation: e.target.value})} className="mt-1"/></div>
                    <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="mt-1"/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Role</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                        <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                        <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r.replace("_"," ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Department</Label>
                      <Select value={form.department_id} onValueChange={(v) => setForm({...form, department_id: v})}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Select"/></SelectTrigger>
                        <SelectContent>{depts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Reporting Manager</Label>
                    <Select value={form.manager_id} onValueChange={(v) => setForm({...form, manager_id: v})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select manager"/></SelectTrigger>
                      <SelectContent>{emps.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="text-xs text-slate-500 bg-cyan-500/5 border border-cyan-500/30 rounded-md p-2">
                    Default password will be: <code className="bg-white px-1 rounded">password123</code>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} disabled={!form.full_name || !form.email} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-new-emp">Add Employee</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Button variant={view==="list"?"default":"outline"} size="sm" onClick={() => setView("list")} className={view==="list"?"bg-slate-900":""}>
            <ListIcon className="w-4 h-4 mr-1"/>List
          </Button>
          <Button variant={view==="card"?"default":"outline"} size="sm" onClick={() => setView("card")} className={view==="card"?"bg-slate-900":""}>
            <LayoutGrid className="w-4 h-4 mr-1"/>Cards
          </Button>
        </div>
      </div>

      <Card className="p-3 bg-slate-50 border-slate-200 shadow-none">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <Input placeholder="Search by name, email, role…" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9 bg-white" data-testid="people-search"/>
          </div>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="w-52 bg-white"><SelectValue placeholder="Department"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={load} className="bg-slate-900 hover:bg-slate-800">Apply</Button>
        </div>
      </Card>

      {emps.length === 0 ? (
        <Card className="border-slate-200 shadow-none"><EmptyState icon={Search} title="No employees found" description="Try clearing the filters or adding a new employee."/></Card>
      ) : view === "list" ? (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <div className="max-h-[65vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-[0.1em] text-slate-500">
                  <th className="px-4 py-3">Name</th><th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Department</th><th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th>
                  {canManage && <th className="px-4 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {emps.map((e) => (
                  <tr key={e.id} onClick={() => nav(`/people/${e.id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          {e.avatar_url && <AvatarImage src={e.avatar_url} alt={e.full_name}/>}
                          <AvatarFallback className="bg-slate-900 text-white text-xs">
                            {e.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-slate-900">{e.full_name}</div>
                          <div className="text-xs text-slate-500">{e.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{e.designation}</td>
                    <td className="px-4 py-3 text-slate-700">{depts.find(d => d.id === e.department_id)?.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{e.location || "—"}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{e.role.replace("_"," ")}</Badge></td>
                    <td className="px-4 py-3">
                      <Badge className={`border-0 capitalize ${e.employment_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{e.employment_status}</Badge>
                    </td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {isSuper && (
                            <Button variant="ghost" size="sm" onClick={(ev) => { ev.stopPropagation(); setResetTarget(e); }} className="text-slate-600 h-7" title="Reset password" data-testid={`reset-pw-${e.id}`}><Key className="w-4 h-4"/></Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={(ev) => del(e.id, ev)} className="text-red-600 h-7"><Trash2 className="w-4 h-4"/></Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {emps.map((e) => (
            <Card key={e.id} onClick={() => nav(`/people/${e.id}`)}
              className="p-5 border-slate-200 shadow-none cursor-pointer hover:-translate-y-[2px] hover:shadow-sm transition-all duration-200">
              <Avatar className="w-14 h-14 mb-3">
                {e.avatar_url && <AvatarImage src={e.avatar_url} alt={e.full_name}/>}
                <AvatarFallback className="bg-slate-900 text-white">
                  {e.full_name.split(" ").map(s => s[0]).slice(0,2).join("")}
                </AvatarFallback>
              </Avatar>
              <div className="font-[Outfit] font-semibold text-slate-900">{e.full_name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{e.designation}</div>
              <div className="text-xs text-slate-400 mt-2">{depts.find(d => d.id === e.department_id)?.name}</div>
              <div className="mt-3 flex items-center gap-2">
                <Badge variant="outline" className="capitalize text-[10px]">{e.role.replace("_"," ")}</Badge>
                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{e.employment_status}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Admin Reset Password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => { if (!v) { setResetTarget(null); setResetPw(""); setResetForce(false); } }}>
        <DialogContent className="max-w-md" data-testid="admin-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for <span className="font-semibold text-slate-900">{resetTarget?.full_name}</span>. This action is audit-logged.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>New password</Label><Input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} className="mt-1.5" autoFocus data-testid="admin-reset-pw"/></div>
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={resetForce} onChange={(e) => setResetForce(e.target.checked)} data-testid="admin-reset-force" />
              Force user to change this password on next login
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={submitAdminReset} disabled={resetBusy || !resetPw} data-testid="admin-reset-submit">{resetBusy ? "Saving…" : "Set new password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
